import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DollarSignIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import type { MilestoneTemplateRow } from '../lib/billingMilestoneTemplates'
import { sumTemplatePercentages } from '../lib/billingMilestoneTemplates'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileSheet,
  mobile,
} from '../components/mobile'

interface BillingMilestoneTemplate {
  id: string
  name: string
  description?: string | null
  milestones: MilestoneTemplateRow[]
  is_active: boolean
  is_system: boolean
  created_at?: string
  updated_at?: string
}

const emptyTemplate = (): BillingMilestoneTemplate => ({
  id: '',
  name: '',
  description: '',
  milestones: [
    { label: '', percentage: 0 },
    { label: '', percentage: 0 },
  ],
  is_active: true,
  is_system: false,
})

export function BillingMilestoneTemplates() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<BillingMilestoneTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editor, setEditor] = useState<{
    isOpen: boolean
    template: BillingMilestoneTemplate
    isNew: boolean
    saving: boolean
  }>({
    isOpen: false,
    template: emptyTemplate(),
    isNew: true,
    saving: false,
  })
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('billing_milestone_templates')
        .select('*')
        .order('name', { ascending: true })
      if (error) throw error
      setTemplates(
        (data || []).map((row: any) => ({
          ...row,
          milestones: Array.isArray(row.milestones) ? row.milestones : [],
        }))
      )
    } catch (e) {
      console.error('Failed to fetch billing milestone templates:', e)
      setMessage({
        type: 'error',
        text: 'Could not load templates. Run migration 20260711_billing_milestone_templates.sql if needed.',
      })
    } finally {
      setLoading(false)
    }
  }

  const openNew = () => {
    setEditor({
      isOpen: true,
      template: emptyTemplate(),
      isNew: true,
      saving: false,
    })
  }

  const openEdit = (t: BillingMilestoneTemplate) => {
    setEditor({
      isOpen: true,
      template: {
        ...t,
        milestones:
          t.milestones?.length > 0
            ? t.milestones.map((m) => ({ ...m }))
            : [{ label: '', percentage: 0 }],
      },
      isNew: false,
      saving: false,
    })
  }

  const close = () => {
    if (editor.saving) return
    setEditor({
      isOpen: false,
      template: emptyTemplate(),
      isNew: true,
      saving: false,
    })
  }

  const updateField = (field: keyof BillingMilestoneTemplate, value: any) => {
    setEditor((prev) => ({
      ...prev,
      template: { ...prev.template, [field]: value },
    }))
  }

  const updateMilestone = (index: number, patch: Partial<MilestoneTemplateRow>) => {
    setEditor((prev) => {
      const milestones = prev.template.milestones.map((m, i) =>
        i === index ? { ...m, ...patch } : m
      )
      return { ...prev, template: { ...prev.template, milestones } }
    })
  }

  const addMilestone = () => {
    setEditor((prev) => ({
      ...prev,
      template: {
        ...prev.template,
        milestones: [...prev.template.milestones, { label: '', percentage: 0 }],
      },
    }))
  }

  const removeMilestone = (index: number) => {
    setEditor((prev) => ({
      ...prev,
      template: {
        ...prev.template,
        milestones: prev.template.milestones.filter((_, i) => i !== index),
      },
    }))
  }

  const saveTemplate = async () => {
    const t = editor.template
    if (!t.name.trim()) {
      setMessage({ type: 'error', text: 'Template name is required.' })
      return
    }
    const rows = t.milestones.filter((m) => (m.label || '').trim() !== '')
    if (rows.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one labeled milestone.' })
      return
    }
    const totalPct = sumTemplatePercentages(rows)
    if (Math.abs(totalPct - 100) > 0.01) {
      setMessage({
        type: 'error',
        text: `Percentages must total 100% (currently ${totalPct.toFixed(1)}%).`,
      })
      return
    }

    try {
      setEditor((prev) => ({ ...prev, saving: true }))
      const payload = {
        name: t.name.trim(),
        description: t.description || null,
        milestones: rows.map((m) => ({
          label: m.label.trim(),
          percentage: Number(m.percentage) || 0,
        })),
        is_active: t.is_active !== false,
        is_system: editor.isNew ? false : !!t.is_system,
      }

      if (editor.isNew) {
        const { error } = await supabase.from('billing_milestone_templates').insert(payload)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('billing_milestone_templates')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', t.id)
        if (error) throw error
      }

      setMessage({ type: 'success', text: 'Template saved.' })
      await fetchTemplates()
      close()
    } catch (e: any) {
      console.error('Failed to save template:', e)
      setMessage({ type: 'error', text: e?.message || 'Failed to save template.' })
    } finally {
      setEditor((prev) => ({ ...prev, saving: false }))
    }
  }

  const deleteTemplate = async (t: BillingMilestoneTemplate) => {
    if (t.is_system) {
      setMessage({ type: 'error', text: 'System templates cannot be deleted. Deactivate them instead.' })
      return
    }
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return
    try {
      const { error } = await supabase.from('billing_milestone_templates').delete().eq('id', t.id)
      if (error) throw error
      setMessage({ type: 'success', text: 'Template deleted.' })
      await fetchTemplates()
    } catch (e: any) {
      console.error('Failed to delete template:', e)
      setMessage({ type: 'error', text: e?.message || 'Failed to delete template.' })
    }
  }

  const formatMilestones = (t: BillingMilestoneTemplate) => {
    if (!t.milestones?.length) return '—'
    return t.milestones.map((m) => `${m.label} ${m.percentage}%`).join(' · ')
  }

  const editorPct = sumTemplatePercentages(editor.template.milestones)

  return (
    <MobilePage>
      <MobilePageHeader
        title="Billing Milestone Templates"
        subtitle="Reusable progress-billing schedules for contracts and proposals"
        onBack={() => navigate('/settings')}
        backLabel="Back to Settings"
        actions={
          <button type="button" onClick={openNew} className={mobile.primaryBtn}>
            <PlusIcon className="w-4 h-4" />
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">New Template</span>
          </button>
        }
      />

      {message && (
        <div
          className={`p-3 rounded-xl border text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading templates...</div>
        ) : templates.length === 0 ? (
          <MobileEmptyState
            icon={<DollarSignIcon className="w-12 h-12" />}
            title="No templates yet"
            description='Tap "New" or run the billing milestone templates migration for seeded presets.'
          />
        ) : (
          templates.map((t) => (
            <MobileListRow
              key={t.id}
              title={t.name}
              subtitle={t.description || undefined}
              badges={
                <>
                  {t.is_system && (
                    <span className={`${mobile.badge} bg-gray-100 text-gray-600`}>System</span>
                  )}
                  <span
                    className={`${mobile.badge} ${
                      t.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </>
              }
              meta={<div className="truncate">{formatMilestones(t)}</div>}
              footer={
                <>
                  <MobileRowAction onClick={() => openEdit(t)}>
                    <PencilIcon className="w-4 h-4" /> Edit
                  </MobileRowAction>
                  {!t.is_system && (
                    <MobileRowAction tone="danger" onClick={() => deleteTemplate(t)}>
                      <TrashIcon className="w-4 h-4" /> Delete
                    </MobileRowAction>
                  )}
                </>
              }
              chevron={false}
            />
          ))
        )}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <DollarSignIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium">No templates yet</p>
            <p className="text-sm mt-1">
              Click &quot;New Template&quot; or run the billing milestone templates migration for
              seeded presets.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Milestones
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {templates.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {t.name}
                      {t.is_system && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                          System
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-xs text-gray-500 mt-0.5 max-w-md truncate">
                        {t.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-lg">{formatMilestones(t)}</td>
                  <td className="px-4 py-3 text-sm">
                    {t.is_active ? (
                      <span className="text-green-700">Active</span>
                    ) : (
                      <span className="text-gray-400">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(t)}
                        className="p-2 text-blue-600 hover:text-blue-800"
                        aria-label="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      {!t.is_system && (
                        <button
                          type="button"
                          onClick={() => deleteTemplate(t)}
                          className="p-2 text-red-600 hover:text-red-800"
                          aria-label="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MobileSheet
        open={editor.isOpen}
        onClose={close}
        title={editor.isNew ? 'New template' : 'Edit template'}
        footer={
          <>
            <button
              type="button"
              disabled={editor.saving}
              onClick={close}
              className={`${mobile.secondaryBtn} flex-1`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={editor.saving}
              onClick={saveTemplate}
              className={`${mobile.primaryBtn} flex-1`}
            >
              {editor.saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className={mobile.label}>Name *</label>
            <input
              type="text"
              value={editor.template.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={mobile.field}
            />
          </div>
          <div>
            <label className={mobile.label}>Description</label>
            <input
              type="text"
              value={editor.template.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              className={mobile.field}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 min-h-[44px]">
            <input
              type="checkbox"
              checked={editor.template.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-gray-300 w-5 h-5"
            />
            Active (shown in Apply template lists)
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Milestones</label>
              <button
                type="button"
                onClick={addMilestone}
                className="min-h-[44px] px-2 text-sm font-semibold text-blue-600"
              >
                Add row
              </button>
            </div>
            <div className="space-y-2">
              {editor.template.milestones.map((m, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={m.label}
                    onChange={(e) => updateMilestone(index, { label: e.target.value })}
                    placeholder="Label"
                    className={`${mobile.field} flex-1`}
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={m.percentage || ''}
                    onChange={(e) =>
                      updateMilestone(index, { percentage: parseFloat(e.target.value) || 0 })
                    }
                    className={`${mobile.field} w-24`}
                  />
                  <span className="text-sm text-gray-500">%</span>
                  {editor.template.milestones.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeMilestone(index)}
                      className="p-2 min-h-[44px] min-w-[44px] text-red-600"
                      aria-label="Remove milestone"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p
              className={`mt-2 text-sm ${
                Math.abs(editorPct - 100) < 0.01 ? 'text-green-700' : 'text-red-600'
              }`}
            >
              Total: {editorPct.toFixed(1)}%
            </p>
          </div>
        </div>
      </MobileSheet>
    </MobilePage>
  )
}
